import {
  put,
  call,
  takeLatest,
  takeEvery
} from "redux-saga/effects";
import {
  uploadFile,
  downloadXLSFile
} from "utils/api";
import {
  defaultReturn,
  makePayload,
  makeReadPayload,
  makeWritePayload,
  generateDefaultActions,
  subKeySaga,
  requestSaga,
  fillDictionary
} from "store/utils/helpers";
import {
  ToastSuccess,
  ToastError
} from "components/Global/Toasts/Toasty";
import _ from "lodash";

const initialState = {
  alerts: {
    details: [],
    ancillary: []
  },
  reminders: {
    details: [],
    ancillary: []
  },
  inAppNotifications: {
    showNewNotificationsIcon: null,
    listOfNotifications: []
  },
  selectedNotification: {
    id: null,
    alertTypeId: null,
    alertTestTypeId: null,
    alertLevelId: null,
    triggeredDate: null,
    companyId: null,
    createdById: null,
    dealId: null,
    fundId: null,
    isActive: true,
    message: null,
    triggerValue: null,
    triggeredDate: null,
    alertContacts: []
  },
  ancillary: []
};

export const RESET_SELECTED_NOTIFICATION = generateDefaultActions("RESET_SELECTED_NOTIFICATION");
export const resetSelectedNotification = payload => makePayload(RESET_SELECTED_NOTIFICATION.value, payload);
function* resetSelectedNotificationSaga() {
  yield put(RESET_SELECTED_NOTIFICATION.createSuccess({
    id: null,
    alertTypeId: null,
    alertTestTypeId: null,
    alertLevelId: null,
    triggeredDate: null,
    companyId: null,
    createdById: null,
    dealId: null,
    fundId: null,
    isActive: true,
    message: null,
    triggerValue: null,
    triggeredDate: null,
    alertContacts: []
  }));
}

export const GET_NOTIFICATIONS_BY_DEAL = generateDefaultActions("GET_NOTIFICATIONS_BY_DEAL");
export const getNotificationsByDeal = payload => makePayload(GET_NOTIFICATIONS_BY_DEAL.value, payload);
function* getNotificationsByDealSaga(payload) {
  const url = "/getAlertsForDeal";
  const key = payload.isAlert ? "alertsByDeal" : "notificationsByDeal";
  const requestSaga = subKeySaga(key);
  yield requestSaga(GET_NOTIFICATIONS_BY_DEAL, url, "POST", payload);
}

export const GET_ALERTS_BY_CONTACT = generateDefaultActions("GET_ALERTS_BY_CONTACT");
export const getAlertsByContact = payload => makePayload(GET_ALERTS_BY_CONTACT.value, payload);
function* getAlertsByContactSaga() {
  const url = "/alertsByType/?type=alert";
  yield requestSaga(GET_ALERTS_BY_CONTACT, url, "GET");
}

export const GET_REMINDERS_BY_CONTACT = generateDefaultActions("GET_REMINDERS_BY_CONTACT");
export const getRemindersByContact = payload => makePayload(GET_REMINDERS_BY_CONTACT.value, payload);
function* getRemindersByContactSaga() {
  const url = "/alertsByType/?type=reminder";
  yield requestSaga(GET_REMINDERS_BY_CONTACT, url, "GET");
}

export const GET_NOTIFICATION_BY_ID = generateDefaultActions("GET_NOTIFICATION_BY_ID");
export const getNotificationById = payload => makePayload(GET_NOTIFICATION_BY_ID.value, payload);
function* getNotificationByIdSaga(payload) {
  const url = `/alerts?id=${payload.id}`;
  yield requestSaga(GET_NOTIFICATION_BY_ID, url, "GET");
}

export const GET_ANCILLARY = generateDefaultActions("GET_ANCILLARY");
export const getAncillary = payload => makePayload(GET_ANCILLARY.value, payload);
function* getAncillarySaga(payload) {
  const url = "/alerts/ancillary/";
  yield requestSaga(GET_ANCILLARY, url, "GET");
}

export const UPSERT_NOTIFICATION = generateDefaultActions("UPSERT_NOTIFICATION");
export const addUpdateNotification = payload => makePayload(UPSERT_NOTIFICATION.value, payload);
function* addUpdateNotificationSaga(payload) {
  const url = "/alerts";
  const callback = payload.callback;
  delete payload.callback;
  yield* requestSaga(UPSERT_NOTIFICATION, url, "POST", payload);
  yield* getAlertsByContactSaga();
  yield* getRemindersByContactSaga();
  yield* resetSelectedNotificationSaga();
  if (callback) {
    callback();
  }
}

export const GET_IN_APP_NOTIFICATIONS = generateDefaultActions("GET_IN_APP_NOTIFICATIONS");
export const getInAppNotificationsByContact = payload => makeReadPayload(GET_IN_APP_NOTIFICATIONS.value, payload);
function* getInAppNotificationsByContactSaga() {
  const url = "/inappnotifications";
  yield requestSaga(GET_IN_APP_NOTIFICATIONS, url, "GET");
}

export const UPDATE_IN_APP_NOTIFICATIONS = generateDefaultActions("UPDATE_IN_APP_NOTIFICATIONS");
export const markAllNotificationsAsRead = payload => makeWritePayload(UPDATE_IN_APP_NOTIFICATIONS.value, payload);
function* markAllNotificationsAsReadSaga(payload) {
  const url = "/inappnotifications";
  yield requestSaga(UPDATE_IN_APP_NOTIFICATIONS, url, "POST", payload);
  yield getInAppNotificationsByContactSaga();
}

export const INITIALIZE_ALERT_ENGINE = generateDefaultActions("INITIALIZE_ALERT_ENGINE");
export const initializeAlertEngine = payload => makeWritePayload(INITIALIZE_ALERT_ENGINE.value, payload);
function* initializeAlertEngineSaga(payload) {
  const url = "/initializeAlertEngine";
  yield requestSaga(INITIALIZE_ALERT_ENGINE, url, "GET");
}

export default function notificationsReducer(state = initialState, action) {
  const { type, payload, subKey } = action;
  switch (type) {
    case UPSERT_NOTIFICATION.success:
      if (payload.updatedDealList) {
        return defaultReturn(state, payload.updatedDealList, "notificationsByDeal");
      }
      break;
    case GET_NOTIFICATIONS_BY_DEAL.success:
      return defaultReturn(state, payload.result, subKey);
    case RESET_SELECTED_NOTIFICATION.success:
      return defaultReturn(state, payload, "selectedNotification");
    case GET_ALERTS_BY_CONTACT.success:
      return defaultReturn(state, payload.data, "alerts");
    case GET_REMINDERS_BY_CONTACT.success:
      return defaultReturn(state, payload.data, "reminders");
    case GET_NOTIFICATION_BY_ID.success:
      if (payload.data.details) {
        return defaultReturn(state, payload.data.details, "selectedNotification");
      }

      return defaultReturn(state, initialState.selectedNotification, "selectedNotification");

    case UPDATE_IN_APP_NOTIFICATIONS.success:
      return defaultReturn(state, initialState.selectedNotification, "selectedNotification");
    case GET_ANCILLARY.success:
      return defaultReturn(state, payload, "ancillary");
    case GET_IN_APP_NOTIFICATIONS.success:
      const key = "in_app_notification_ids";
      const unseenNotifications = payload.data.details.listOfNotifications.filter(n => !n.isViewedByUser);
      const notificationsToBeMarkedAsViewed = unseenNotifications.map(n => n.id);
      localStorage.setItem(key, JSON.stringify(notificationsToBeMarkedAsViewed));

      return defaultReturn(state, payload.data, "inAppNotifications");
    default:
      return state;
  }
}

export function* notificationsSaga() {
  yield takeLatest(RESET_SELECTED_NOTIFICATION.value, resetSelectedNotificationSaga);
  yield takeLatest(GET_ALERTS_BY_CONTACT.value, getAlertsByContactSaga);
  yield takeLatest(GET_REMINDERS_BY_CONTACT.value, getRemindersByContactSaga);
  yield takeLatest(UPSERT_NOTIFICATION.value, addUpdateNotificationSaga);
  yield takeLatest(GET_IN_APP_NOTIFICATIONS.value, getInAppNotificationsByContactSaga);
  yield takeLatest(UPDATE_IN_APP_NOTIFICATIONS.value, markAllNotificationsAsReadSaga);
  yield takeLatest(GET_NOTIFICATION_BY_ID.value, getNotificationByIdSaga);
  yield takeLatest(GET_ANCILLARY.value, getAncillarySaga);
  yield takeLatest(INITIALIZE_ALERT_ENGINE.value, initializeAlertEngineSaga);
  yield takeEvery(GET_NOTIFICATIONS_BY_DEAL.value, getNotificationsByDealSaga);
}
